// =============================================================================
// One source of truth for "which pages can the user open right now".
//
// Honours the same rules the sidebar applies: features switched off in
// Settings disappear, the personal calendar follows its own toggle, and
// shared calendars are injected right after it. Used by the sidebar, the
// mobile tab bar and the tab-bar picker in Settings, so all three can never
// disagree about what exists.
// =============================================================================

import { Users as UsersIcon } from 'lucide-react';
import { FEATURE_GROUPS, getGroupById } from '../featureRegistry';

// [{ groupId, groupLabel, items: [{ id, label, rawLabel, icon }] }]
export const buildNavSections = (data) => {
  const settings = data?.settings || {};
  const enabled = settings.enabledFeatures || {};
  const showPersonalCalendar = settings.showPersonalCalendar !== false;
  const sharedCalendars = data?.calendar?.sharedCalendars || [];
  const order =
    settings.groupOrder && settings.groupOrder.length > 0
      ? settings.groupOrder
      : FEATURE_GROUPS.map((g) => g.id);

  const sections = [];
  for (const gid of order) {
    const group = getGroupById(gid);
    if (!group) continue;

    let items = (group.items || []).filter((it) => enabled[it.id] !== false);

    if (group.id === 'planning') {
      if (!showPersonalCalendar) {
        items = items.filter((it) => it.id !== 'calendar');
      }
      const sharedItems = sharedCalendars
        .filter((sc) => enabled[`shared:${sc.id}`] !== false)
        .map((sc) => ({
          id: `shared:${sc.id}`,
          label: sc.name,
          rawLabel: true,
          icon: UsersIcon
        }));
      const calIdx = items.findIndex((it) => it.id === 'calendar');
      items =
        calIdx >= 0
          ? [...items.slice(0, calIdx + 1), ...sharedItems, ...items.slice(calIdx + 1)]
          : [...sharedItems, ...items];
    }

    if (items.length > 0) {
      sections.push({ groupId: group.id, groupLabel: group.label, items });
    }
  }
  return sections;
};

export const flatNavItems = (data) =>
  buildNavSections(data).flatMap((s) => s.items);

// The four pages in the tab bar. Stored per user as a list of item ids; ids
// that no longer exist (feature switched off, calendar left) are dropped and
// backfilled from the top of the menu so the bar is never half empty.
export const TAB_SLOTS = 4;

export const resolveTabs = (data) => {
  const items = flatNavItems(data);
  const byId = new Map(items.map((it) => [it.id, it]));
  const picked = (data?.settings?.navTabs || []).filter((id) => byId.has(id));

  // A deliberate choice is honoured exactly: pick three pages and the bar
  // shows three (plus Mehr), evenly spaced. Only when nothing has been
  // chosen — or every pick has since disappeared — do we fill from the top
  // of the menu, so the bar is never empty.
  const chosen = picked.length
    ? picked.slice(0, TAB_SLOTS)
    : items.slice(0, TAB_SLOTS).map((it) => it.id);

  return chosen.map((id) => byId.get(id)).filter(Boolean);
};
